import React, { useState } from "react";
import { Plus, X, Camera, MessageSquare, MapPinned, ListChecks, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitAmendment } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { AttachmentPicker } from "./AttachmentPicker";

/**
 * Iter66 (Phase 4) — Citizen amendment modal.
 *
 * Lets the citizen who reported an alert come back later and:
 *   • add or correct a description,
 *   • attach new photo / audio / video evidence,
 *   • request a category correction (NEVER auto-reassigns),
 *   • clarify the location (original GPS preserved verbatim),
 *   • leave a freeform citizen note.
 *
 * Hits POST /api/alerts/{id}/amendments with the reporter_phone as the
 * ownership gate. Original alert is never modified — every submission
 * is an append-only row in `alert_amendments`. Real-time fan-out is
 * handled server-side (admin + assigned-agency + citizen rooms).
 */
const CHANGE_TYPES = [
  { value: "evidence_added",                 label: "Ajouter une preuve",          icon: Camera,      hint: "Photo, audio ou vidéo." },
  { value: "description_added",              label: "Ajouter une information",     icon: MessageSquare, hint: "Détail supplémentaire pour aider les secours." },
  { value: "description_corrected",          label: "Corriger la description",     icon: MessageSquare, hint: "Précisez ou corrigez ce que vous avez signalé." },
  { value: "location_clarification_added",   label: "Préciser le lieu",            icon: MapPinned,   hint: "L'emplacement GPS original sera conservé." },
  { value: "category_correction_requested",  label: "Catégorie incorrecte",        icon: ListChecks,  hint: "Le Central Admin décidera de la réaffectation." },
  { value: "citizen_note_added",             label: "Ajouter une note",            icon: MessageSquare, hint: "Note libre à l'attention du Central Admin." },
];

export function AmendmentModal({ alertId, open, onClose, onSubmitted, currentCategory }) {
  const [step, setStep] = useState("pick"); // "pick" | "form"
  const [changeType, setChangeType] = useState(null);
  const [note, setNote] = useState("");
  const [requestedCategory, setRequestedCategory] = useState("");
  const [locationClarification, setLocationClarification] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("pick");
    setChangeType(null);
    setNote("");
    setRequestedCategory("");
    setLocationClarification("");
    setAttachments([]);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const pick = (ct) => {
    setChangeType(ct);
    setStep("form");
  };

  const submit = async () => {
    const phone = userIdentity.getPhone();
    if (!phone) { toast.error("Identité introuvable. Rouvrez l'application."); return; }
    if (!changeType) return;

    // Per-type validation so the citizen knows what's missing.
    if (changeType.value === "category_correction_requested" && !requestedCategory.trim()) {
      toast.error("Précisez la catégorie correcte."); return;
    }
    if (changeType.value === "location_clarification_added" && !locationClarification.trim()) {
      toast.error("Précisez le lieu."); return;
    }
    if (["evidence_added", "photo_added", "audio_added", "video_added"].includes(changeType.value) && attachments.length === 0) {
      toast.error("Ajoutez au moins une pièce jointe."); return;
    }
    if (["description_added", "description_corrected", "citizen_note_added"].includes(changeType.value) && !note.trim()) {
      toast.error("Ajoutez un texte."); return;
    }

    setBusy(true);
    try {
      const result = await submitAmendment(alertId, {
        reporter_phone: phone,
        change_type: changeType.value,
        note: note || null,
        requested_category: requestedCategory || null,
        location_clarification: locationClarification || null,
        attachments,
      });
      toast.success("Mise à jour envoyée");
      onSubmitted?.(result);
      reset();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 h-[100dvh] z-[100] bg-rescue-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      data-testid="amendment-modal"
      onClick={close}
    >
      {/* Iter87 (P0 fix) — Robust 3-tier flex layout: header stays
          visible, ONLY the middle body scrolls (`flex-1 min-h-0
          overflow-y-auto`), and the footer holding the final
          Envoyer/Retour buttons is pinned outside the scroll region so
          it never depends on scroll math to stay reachable. Card height
          is capped with `dvh` (dynamic viewport unit) instead of
          `vh`/`max-h-full` so it tracks the REAL usable viewport on iOS/
          Android, including when the on-screen keyboard shrinks it.
          Root cause of the original bug: this modal shared the exact
          same z-50 as <BottomNav>, which mounts AFTER it in the DOM
          (App.js) — same z-index + later paint order meant the opaque
          bottom nav rendered ON TOP of the modal's submit button,
          independent of scroll position. Bumped to z-[100] above. */}
      <div
        className="w-full sm:max-w-md bg-white border-2 border-rescue-ink shadow-xl max-h-[90dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b-2 border-rescue-line bg-rescue-bg shrink-0">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rescue-mute">Mise à jour</div>
            <div className="text-base font-display font-black tracking-tight">
              {step === "pick" ? "Compléter l'alerte" : changeType?.label}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="amendment-modal-close"
            disabled={busy}
            className="p-1.5 hover:bg-rescue-line disabled:opacity-40"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
        {step === "pick" && (
          <div className="p-3 space-y-2" data-testid="amendment-picker">
            <p className="text-xs text-rescue-mute mb-2">
              Que souhaitez-vous ajouter ou corriger ? Votre alerte d'origine reste intacte.
            </p>
            {CHANGE_TYPES.map((ct) => {
              const Icon = ct.icon;
              return (
                <button
                  key={ct.value}
                  type="button"
                  onClick={() => pick(ct)}
                  data-testid={`amendment-pick-${ct.value}`}
                  className="w-full text-left px-3 py-2.5 border-2 border-rescue-line hover:border-rescue-ink hover:bg-rescue-bg transition-colors flex items-start gap-3"
                >
                  <Icon className="w-4 h-4 mt-0.5 text-rescue-ink shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold text-rescue-ink uppercase tracking-[0.1em]">{ct.label}</div>
                    <div className="text-[11px] text-rescue-mute mt-0.5">{ct.hint}</div>
                  </div>
                  <Plus className="w-4 h-4 text-rescue-mute shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}

        {step === "form" && changeType && (
          <div className="p-3 space-y-3" data-testid="amendment-form">
            {/* Note / description — required for most types, optional for the rest */}
            {["description_added", "description_corrected", "citizen_note_added", "evidence_added"].includes(changeType.value) && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">
                  {changeType.value === "description_corrected" ? "Description corrigée" : "Texte"}
                </label>
                <textarea
                  data-testid="amendment-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ajoutez vos précisions ici…"
                  rows={4}
                  maxLength={2000}
                  className="mt-1 w-full border-2 border-rescue-line bg-white px-2 py-1.5 text-[13px] focus:border-rescue-ink focus:outline-none resize-none"
                />
                <div className="text-[10px] text-rescue-mute mt-1">{note.length}/2000</div>
              </div>
            )}

            {/* Category correction — show CURRENT then ask for the suggested one */}
            {changeType.value === "category_correction_requested" && (
              <>
                {currentCategory && (
                  <div className="text-[11px] text-rescue-mute">
                    Catégorie actuelle : <span className="font-bold text-rescue-ink">{currentCategory}</span>
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">
                    Catégorie correcte (suggestion)
                  </label>
                  <input
                    type="text"
                    data-testid="amendment-requested-category"
                    value={requestedCategory}
                    onChange={(e) => setRequestedCategory(e.target.value)}
                    placeholder="Ex. Incendie, Médical, Police…"
                    maxLength={80}
                    className="mt-1 w-full border-2 border-rescue-line bg-white px-2 py-1.5 text-[13px] focus:border-rescue-ink focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">Raison (facultatif)</label>
                  <textarea
                    data-testid="amendment-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Expliquez pourquoi la catégorie est incorrecte…"
                    rows={3}
                    maxLength={2000}
                    className="mt-1 w-full border-2 border-rescue-line bg-white px-2 py-1.5 text-[13px] focus:border-rescue-ink focus:outline-none resize-none"
                  />
                </div>
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 p-2">
                  La réaffectation est décidée par le Central Admin. Votre alerte continue d'être traitée.
                </div>
              </>
            )}

            {/* Location clarification — original GPS preserved */}
            {changeType.value === "location_clarification_added" && (
              <>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-rescue-mute">
                    Précision sur le lieu
                  </label>
                  <textarea
                    data-testid="amendment-location-clarification"
                    value={locationClarification}
                    onChange={(e) => setLocationClarification(e.target.value)}
                    placeholder="Ex. À l'arrière du bâtiment, près du marché Yirimadio…"
                    rows={3}
                    maxLength={500}
                    className="mt-1 w-full border-2 border-rescue-line bg-white px-2 py-1.5 text-[13px] focus:border-rescue-ink focus:outline-none resize-none"
                  />
                </div>
                <div className="text-[10px] text-rescue-mute italic">
                  Votre position GPS d'origine sera conservée.
                </div>
              </>
            )}

            {/* Attachments — always shown for evidence_added, optional otherwise */}
            {["evidence_added", "photo_added", "audio_added", "video_added", "description_added", "description_corrected"].includes(changeType.value) && (
              <AttachmentPicker
                attachments={attachments}
                onChange={setAttachments}
                testidPrefix="amendment-attach"
                max={6}
              />
            )}
          </div>
        )}
        </div>

        {step === "form" && changeType && (
          <div
            className="flex gap-2 p-3 border-t-2 border-rescue-line bg-white shrink-0"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={() => setStep("pick")}
              disabled={busy}
              data-testid="amendment-back"
              className="px-3 py-2.5 border-2 border-rescue-line text-rescue-ink text-[11px] font-bold uppercase tracking-[0.12em] hover:border-rescue-ink disabled:opacity-40"
            >
              Retour
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="amendment-submit"
              className="flex-1 px-3 py-2.5 bg-rescue-ink text-white text-[11px] font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {busy ? "Envoi en cours…" : "Envoyer la mise à jour"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
